import apiClient from "@shared/config/axiosConfig";

/**
 * Servicio para gestión del Plan Anual de Actividades
 */
const planAnualService = {
  // ==================== COMPONENTES DEL SISTEMA ====================

  /**
   * Obtener todos los componentes del sistema
   * @returns {Promise<Array>} Lista de componentes
   */
  getComponentesSistema: async () => {
    try {
      const response = await apiClient.get("/componentes-sistema");
      return response.data;
    } catch (error) {
      console.error("Error obteniendo componentes del sistema:", error);
      throw error;
    }
  },

  /**
   * Obtener componente por ID
   * @param {number} id - ID del componente
   * @returns {Promise<Object>} Componente
   */
  getComponenteSistemaById: async (id) => {
    try {
      const response = await apiClient.get(`/componentes-sistema/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error obteniendo componente ${id}:`, error);
      throw error;
    }
  },

  /**
   * Crear nuevo componente del sistema
   * @param {Object} componenteData - { descripcion, activo }
   * @returns {Promise<Object>} Componente creado
   */
  createComponenteSistema: async (componenteData) => {
    try {
      const response = await apiClient.post(
        "/componentes-sistema",
        componenteData,
      );
      return response.data;
    } catch (error) {
      console.error("Error creando componente:", error);
      throw error;
    }
  },

  /**
   * Actualizar componente del sistema
   * @param {number} id - ID del componente
   * @param {Object} componenteData - Datos actualizados
   * @returns {Promise<Object>} Componente actualizado
   */
  updateComponenteSistema: async (id, componenteData) => {
    try {
      const response = await apiClient.put(
        `/componentes-sistema/${id}`,
        componenteData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error actualizando componente ${id}:`, error);
      throw error;
    }
  },

  /**
   * Eliminar componente del sistema
   * @param {number} id - ID del componente
   * @returns {Promise<Object>} Confirmación
   */
  deleteComponenteSistema: async (id) => {
    try {
      const response = await apiClient.delete(`/componentes-sistema/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error eliminando componente ${id}:`, error);
      throw error;
    }
  },

  // ==================== SUBCOMPONENTES POR PLANTA ====================

  /**
   * Obtener subcomponentes de una planta
   * @param {number} idPlanta - ID de la planta
   * @returns {Promise<Array>} Lista de subcomponentes
   */
  getSubcomponentesPorPlanta: async (idPlanta) => {
    try {
      const response = await apiClient.get(
        `/plantas/${idPlanta}/subcomponentes`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo subcomponentes de planta ${idPlanta}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Obtener estructura completa de componentes y subcomponentes
   * @param {number} idPlanta - ID de la planta
   * @returns {Promise<Object>} Estructura jerárquica
   */
  getEstructuraCompleta: async (idPlanta) => {
    try {
      const response = await apiClient.get(
        `/plantas/${idPlanta}/componentes-configurados`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo estructura de planta ${idPlanta}:`,
        error.response?.data || error.message,
      );
      throw error;
    }
  },

  /**
   * Crear subcomponente para una planta
   * @param {number} idPlanta - ID de la planta
   * @param {Object} subcomponenteData - { idcomponentesistema, descripcion, orden }
   * @returns {Promise<Object>} Subcomponente creado
   */
  createSubcomponente: async (idPlanta, subcomponenteData) => {
    try {
      const response = await apiClient.post(
        `/plantas/${idPlanta}/subcomponentes`,
        subcomponenteData,
      );
      return response.data;
    } catch (error) {
      console.error("Error creando subcomponente:", error);
      throw error;
    }
  },

  /**
   * Actualizar subcomponente
   * @param {number} id - ID del subcomponente
   * @param {Object} subcomponenteData - Datos actualizados
   * @returns {Promise<Object>} Subcomponente actualizado
   */
  updateSubcomponente: async (id, subcomponenteData) => {
    try {
      const response = await apiClient.put(
        `/subcomponentes/${id}`,
        subcomponenteData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error actualizando subcomponente ${id}:`, error);
      throw error;
    }
  },

  /**
   * Eliminar subcomponente
   * @param {number} id - ID del subcomponente
   * @returns {Promise<Object>} Confirmación
   */
  deleteSubcomponente: async (id) => {
    try {
      const response = await apiClient.delete(`/subcomponentes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error eliminando subcomponente ${id}:`, error);
      throw error;
    }
  },

  // ==================== PLANTILLAS DE ACTIVIDAD ====================

  /**
   * Obtener plantillas de una planta por año
   * @param {number} idPlanta - ID de la planta
   * @param {number} anio - Año
   * @returns {Promise<Array>} Lista de plantillas
   */
  getPlantillasPorPlanta: async (idPlanta, anio) => {
    try {
      const response = await apiClient.get(`/plantas/${idPlanta}/plantillas`, {
        params: { anio },
      });
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo plantillas de planta ${idPlanta}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Obtener plantilla por ID
   * @param {number} id - ID de la plantilla
   * @returns {Promise<Object>} Plantilla con subcomponentes
   */
  getPlantillaById: async (id) => {
    try {
      const response = await apiClient.get(`/plantillas/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error obteniendo plantilla ${id}:`, error);
      throw error;
    }
  },

  /**
   * Crear nueva plantilla
   * @param {number} idPlanta - ID de la planta
   * @param {Object} plantillaData - Datos de la plantilla
   * @returns {Promise<Object>} Plantilla creada
   */
  createPlantilla: async (idPlanta, plantillaData) => {
    try {
      const response = await apiClient.post(
        `/plantas/${idPlanta}/plantillas`,
        plantillaData,
      );
      return response.data;
    } catch (error) {
      console.error("Error creando plantilla:", error);
      throw error;
    }
  },

  /**
   * Actualizar plantilla
   * @param {number} id - ID de la plantilla
   * @param {Object} plantillaData - Datos actualizados
   * @returns {Promise<Object>} Plantilla actualizada
   */
  updatePlantilla: async (id, plantillaData) => {
    try {
      const response = await apiClient.put(`/plantillas/${id}`, plantillaData);
      return response.data;
    } catch (error) {
      console.error(`Error actualizando plantilla ${id}:`, error);
      throw error;
    }
  },

  /**
   * Eliminar plantilla
   * @param {number} id - ID de la plantilla
   * @returns {Promise<Object>} Confirmación
   */
  deletePlantilla: async (id) => {
    try {
      const response = await apiClient.delete(`/plantillas/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error eliminando plantilla ${id}:`, error);
      throw error;
    }
  },

  /**
   * Clonar plantilla para otro año
   * @param {number} id - ID de la plantilla a clonar
   * @param {Object} cloneData - { nuevoAnio, ajustes }
   * @returns {Promise<Object>} Plantilla clonada
   */
  clonarPlantilla: async (id, cloneData) => {
    try {
      const response = await apiClient.post(
        `/plantillas/${id}/clonar`,
        cloneData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error clonando plantilla ${id}:`, error);
      throw error;
    }
  },

  // ==================== ACTIVIDADES PROGRAMADAS ====================

  /**
   * Generar actividades desde plantilla
   * @param {number} idPlantilla - ID de la plantilla
   * @param {number} anio - Año para generar las actividades
   * @param {string} fechainicio - Fecha de inicio (opcional)
   * @returns {Promise<Object>} Resultado con actividades generadas
   */
  generarActividades: async (idPlantilla, anio, fechainicio = null) => {
    try {
      const body = { anio };
      if (fechainicio) {
        body.fechainicio = fechainicio;
      }
      const response = await apiClient.post(
        `/plantillas/${idPlantilla}/generar-actividades`,
        body,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error generando actividades de plantilla ${idPlantilla}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Eliminar actividades generadas de una plantilla
   * @param {number} idPlantilla - ID de la plantilla
   * @param {number} anio - Año de las actividades a eliminar
   * @returns {Promise<Object>} Resultado con cantidad eliminada
   */
  eliminarActividadesPlantilla: async (idPlantilla, anio) => {
    try {
      const response = await apiClient.delete(
        `/plantillas/${idPlantilla}/actividades`,
        {
          params: { anio },
        },
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error eliminando actividades de plantilla ${idPlantilla}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Obtener actividades programadas con filtros
   * @param {number} idPlanta - ID de la planta
   * @param {Object} filtros - { anio, estado, idtipomantenimiento, semana }
   * @returns {Promise<Array>} Lista de actividades
   */
  getActividadesProgramadas: async (idPlanta, filtros = {}) => {
    try {
      const response = await apiClient.get(`/plantas/${idPlanta}/actividades`, {
        params: filtros,
      });
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo actividades de planta ${idPlanta}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Obtener actividad por ID
   * @param {number} id - ID de la actividad
   * @returns {Promise<Object>} Actividad con subcomponentes
   */
  getActividadById: async (id) => {
    try {
      const response = await apiClient.get(`/actividades/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error obteniendo actividad ${id}:`, error);
      throw error;
    }
  },

  /**
   * Actualizar actividad programada (cualquier tipo)
   * @param {number} id - ID de la actividad
   * @param {Object} datos - { titulo?, descripcion?, fechainicio?, fechafin?, estado?, observaciones? }
   * @returns {Promise<Object>} Actividad actualizada
   */
  actualizarActividad: async (id, datos) => {
    try {
      const response = await apiClient.put(`/actividades/${id}`, datos);
      return response.data;
    } catch (error) {
      console.error(`Error actualizando actividad ${id}:`, error);
      throw error;
    }
  },

  /**
   * Obtener actividades pendientes de una planta
   * @param {number} idPlanta - ID de la planta
   * @param {number} limite - Límite de resultados (opcional)
   * @returns {Promise<Array>} Lista de actividades pendientes
   */
  getActividadesPendientes: async (idPlanta, limite = null) => {
    try {
      const params = limite ? { limite } : {};
      const response = await apiClient.get(
        `/plantas/${idPlanta}/actividades/pendientes`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo actividades pendientes de planta ${idPlanta}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Vincular manualmente una actividad con un reporte
   * @param {number} idActividad - ID de la actividad programada
   * @param {number} idReporte - ID del reporte a vincular
   * @returns {Promise<Object>} Actividad actualizada con todos sus reportes
   */
  vincularConReporte: async (idActividad, idReporte) => {
    try {
      const response = await apiClient.post(
        `/actividades/${idActividad}/vincular-reporte`,
        { idreporte: idReporte },
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error vinculando actividad ${idActividad} con reporte ${idReporte}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Desvincular un reporte de una actividad
   * @param {number} idActividad - ID de la actividad
   * @param {number} idReporte - ID del reporte a desvincular
   */
  desvincularReporte: async (idActividad, idReporte) => {
    try {
      const response = await apiClient.delete(
        `/actividades/${idActividad}/reportes/${idReporte}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error desvinculando reporte ${idReporte} de actividad ${idActividad}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Obtener todos los reportes vinculados a una actividad
   * @param {number} idActividad - ID de la actividad
   * @returns {Promise<Array>} Lista de reportes vinculados
   */
  getReportesDeActividad: async (idActividad) => {
    try {
      const response = await apiClient.get(
        `/actividades/${idActividad}/reportes`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo reportes de actividad ${idActividad}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Obtener actividades vinculables de una planta
   * Incluye PENDIENTE, EN_PROGRESO y COMPLETADA
   * @param {number} idPlanta - ID de la planta
   * @returns {Promise<Array>} Lista de actividades vinculables
   */
  getActividadesVinculables: async (idPlanta, idTipoMantenimiento = null) => {
    try {
      const params = idTipoMantenimiento
        ? { idtipomantenimiento: idTipoMantenimiento }
        : {};
      const response = await apiClient.get(
        `/plantas/${idPlanta}/actividades/vinculables`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo actividades vinculables de planta ${idPlanta}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Actualizar estado de actividad
   * @param {number} id - ID de la actividad
   * @param {Object} estadoData - { estado, observaciones }
   * @returns {Promise<Object>} Actividad actualizada
   */
  updateEstadoActividad: async (id, estadoData) => {
    try {
      const response = await apiClient.put(
        `/actividades/${id}/estado`,
        estadoData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error actualizando estado de actividad ${id}:`, error);
      throw error;
    }
  },

  /**
   * Reprogramar actividad
   * @param {number} id - ID de la actividad
   * @param {Object} reprogramacionData - { nuevaSemana, motivo }
   * @returns {Promise<Object>} Actividad reprogramada
   */
  reprogramarActividad: async (id, reprogramacionData) => {
    try {
      const response = await apiClient.put(
        `/actividades/${id}/reprogramar`,
        reprogramacionData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error reprogramando actividad ${id}:`, error);
      throw error;
    }
  },

  /**
   * Obtener historial de cambios de una actividad
   * @param {number} id - ID de la actividad
   * @returns {Promise<Array>} Historial de cambios
   */
  getHistorialActividad: async (id) => {
    try {
      const response = await apiClient.get(`/actividades/${id}/historial`);
      return response.data;
    } catch (error) {
      console.error(`Error obteniendo historial de actividad ${id}:`, error);
      throw error;
    }
  },

  // ==================== PLAN ANUAL - VISUALIZACIÍ“N ====================

  /**
   * Obtener calendario anual completo (46 semanas)
   * @param {number} idPlanta - ID de la planta
   * @param {number} anio - Año
   * @returns {Promise<Object>} Estructura del calendario anual
   */
  getPlanAnualCompleto: async (idPlanta, anio) => {
    try {
      const response = await apiClient.get(
        `/plantas/${idPlanta}/plan-anual/${anio}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo plan anual de planta ${idPlanta} año ${anio}:`,
        error.response?.data || error.message,
      );
      throw error;
    }
  },

  /**
   * Obtener estadísticas de cumplimiento
   * @param {number} idPlanta - ID de la planta
   * @param {number} anio - Año
   * @returns {Promise<Object>} Estadísticas de cumplimiento
   */
  getEstadisticasCumplimiento: async (idPlanta, anio) => {
    try {
      const response = await apiClient.get(
        `/plantas/${idPlanta}/plan-anual/${anio}/estadisticas`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo estadísticas de planta ${idPlanta} año ${anio}:`,
        error.response?.data || error.message,
      );
      throw error;
    }
  },

  /**
   * Obtener actividades por semana específica
   * @param {number} idPlanta - ID de la planta
   * @param {number} semana - Número de semana (1-46)
   * @returns {Promise<Array>} Actividades de la semana
   */
  getActividadesPorSemana: async (idPlanta, semana) => {
    try {
      const response = await apiClient.get(
        `/plantas/${idPlanta}/actividades/semana/${semana}`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error obteniendo actividades de semana ${semana}:`, error);
      throw error;
    }
  },

  /**
   * Generar plan completo del año (todas las plantillas)
   * @param {number} idPlanta - ID de la planta
   * @param {number} anio - Año
   * @returns {Promise<Object>} Resultado de generación masiva
   */
  generarPlanCompleto: async (idPlanta, anio) => {
    try {
      const response = await apiClient.post(
        `/plantas/${idPlanta}/plan-anual/${anio}/generar`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error generando plan completo de planta ${idPlanta} año ${anio}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Importar plan anual completo de una planta a otra
   * Copia subcomponentes, plantillas y actividades. Sobreescribe datos existentes en destino.
   * Si se proporcionan frecuencias, solo importa las de esas frecuencias (parcial).
   * @param {number} idPlantaOrigen - ID de la planta origen
   * @param {number} idPlantaDestino - ID de la planta destino
   * @param {number} anio - Año del plan a importar
   * @param {number[]|null} frecuencias - IDs de frecuencias a filtrar (null = todas)
   * @returns {Promise<Object>} Resultado con resumen de la importación
   */
  importarPlanAnual: async (
    idPlantaOrigen,
    idPlantaDestino,
    anio,
    frecuencias = null,
  ) => {
    try {
      const body = { anio };
      if (Array.isArray(frecuencias) && frecuencias.length > 0) {
        body.frecuencias = frecuencias;
      }
      const response = await apiClient.post(
        `/plantas/${idPlantaOrigen}/importar-plan-anual/${idPlantaDestino}`,
        body,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error importando plan anual de planta ${idPlantaOrigen} a ${idPlantaDestino}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Eliminar TODAS las actividades de un plan anual (planta + año)
   * @param {number} idPlanta - ID de la planta
   * @param {number} anio - Año
   * @returns {Promise<Object>} Resultado con cantidad eliminada
   */
  eliminarTodasActividades: async (idPlanta, anio) => {
    try {
      const response = await apiClient.delete(
        `/plantas/${idPlanta}/plan-anual/${anio}/actividades`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error eliminando todas las actividades de planta ${idPlanta} año ${anio}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Generar actividades masivamente desde TODAS las plantillas de una planta/año.
   * Omite plantillas que ya tienen actividades generadas.
   * @param {number} idPlanta - ID de la planta
   * @param {number} anio - Año
   * @returns {Promise<Object>} Resultado con detalle por plantilla
   */
  generarTodasActividades: async (idPlanta, anio) => {
    try {
      const response = await apiClient.post(
        `/plantas/${idPlanta}/plan-anual/${anio}/generar-todas`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error generando todas las actividades de planta ${idPlanta} año ${anio}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Actualizar masivamente la fecha de inicio de todas las plantillas
   * @param {number} idPlanta - ID de la planta
   * @param {number} anio - Año
   * @param {string} nuevaFechaInicio - Nueva fecha de inicio (YYYY-MM-DD)
   * @returns {Promise<Object>} Resultado con cantidad actualizada
   */
  actualizarFechasMasivo: async (idPlanta, anio, nuevaFechaInicio) => {
    try {
      const response = await apiClient.put(
        `/plantas/${idPlanta}/plantillas/fechas-masivo`,
        { anio, nuevaFechaInicio },
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error actualizando fechas masivamente en planta ${idPlanta} año ${anio}:`,
        error,
      );
      throw error;
    }
  },
};

export default planAnualService;
